import { z } from 'zod'

import { HomeQualityCheckStatus } from '@/types/HomeQualityCheck'

import { InitialFilterState } from './filters.data'

export const marketSegmentFilterSchema = z.object({
  fieldOperationsHubs: z.array(z.string()).nullable(),
  fieldOperationsRegions: z.array(z.string()).nullable(),
  marketSegmentIds: z.array(z.number()).nullable(),
  marketSegmentTypes: z.array(z.string()).nullable(),
})

export const homeQualityChecksFiltersSchema = z.object({
  homeQualityCheckStatus: z.nativeEnum(HomeQualityCheckStatus).nullable(),
})

export const notificationsFiltersSchema = z.object({
  status: z.string().nullable(),
})

export const reservationsFiltersSchema = z.object({
  autopilot: z.boolean().optional(),
  approved: z.boolean().optional(),
  currentReservations: z.boolean().optional(),
  flightxInProgress: z.boolean().optional(),
  lexit: z.boolean().optional(),
  moveIn3days: z.boolean().optional(),
  moveIn48Hrs: z.boolean().optional(),
  moveIn7Days: z.boolean().optional(),
  moveInToday: z.boolean().optional(),
  pending: z.boolean().optional(),
})

export const homeFiltersSchema = z.object({
  autopilot: z.boolean(),
  blocked: z.boolean(),
  direct: z.boolean(),
  leased: z.boolean(),
  newOD: z.boolean(),
  occupied: z.boolean(),
  scheduledForDeinstall: z.boolean(),
  standard: z.boolean(),
  published: z.boolean(),
  installed: z.boolean(),
  vacant: z.boolean(),
})

export const propertiesFiltersSchema = z.object({
  blocked: z.boolean(),
  new: z.boolean(),
})

export const jobsFiltersSchema = z.object({
  cancelled: z.boolean(),
  claimed: z.boolean(),
  completed: z.boolean(),
  future: z.boolean(),
  next7Days: z.boolean(),
  unclaimed: z.boolean(),
})

export const scheduledCommunicationsFiltersSchema = z.object({
  scheduledDatetime: z
    .object({
      start: z.date().optional(),
      end: z.date().optional(),
    })
    .nullable(),
  scheduledCommunicationStatuses: z.array(z.string()).nullable(),
})

const reservationMoveOutRangeSchema = z.object({
  moveOutDate: z.date().optional(),
  moveOutMax: z.date().optional(),
})

const reservationMoveInRangeSchema = z.object({
  moveInDate: z.date().optional(),
  moveInMax: z.date().optional(),
})

export type ReservationsFilters = z.infer<typeof reservationsFiltersSchema>

export type ReservationMoveOutRange = z.infer<typeof reservationMoveOutRangeSchema>

export type ReservationMoveInRange = z.infer<typeof reservationMoveInRangeSchema>

export type PropertiesFilters = z.infer<typeof propertiesFiltersSchema>

export type HomesFilters = z.infer<typeof homeFiltersSchema>

export type HomeQualityChecksFilters = z.infer<typeof homeQualityChecksFiltersSchema>

export type JobsFilters = z.infer<typeof jobsFiltersSchema>

export type ScheduledCommunicationsFilters = z.infer<typeof scheduledCommunicationsFiltersSchema>

export type MarketSegmentFilter = z.infer<typeof marketSegmentFilterSchema>

export type NotificationAlertsFilters = z.infer<typeof notificationsFiltersSchema>

const FiltersContextStateSchema = z.object({
  homeQualityChecksFilters: homeQualityChecksFiltersSchema,
  homesFilters: homeFiltersSchema,
  jobsFilters: jobsFiltersSchema,
  marketSegmentFilter: marketSegmentFilterSchema,
  notificationsFilters: notificationsFiltersSchema,
  propertiesFilters: propertiesFiltersSchema,
  reservationMinLengthOfStay: z.number().optional(),
  reservationMoveOutRange: reservationMoveOutRangeSchema,
  reservationMoveInRange: reservationMoveInRangeSchema,
  reservationsFilters: reservationsFiltersSchema,
  scheduledCommunicationsFilters: scheduledCommunicationsFiltersSchema,
  searchString: z.string().nullable(),
})

export type FiltersContextState = z.infer<typeof FiltersContextStateSchema>

export type FilterNames =
  | 'reservations'
  | 'properties'
  | 'homes'
  | 'homeQualityChecks'
  | 'jobs'
  | 'notifications'
  | 'scheduledCommunications'

export type Filters =
  | Partial<ReservationsFilters>
  | Partial<HomesFilters>
  | Partial<HomeQualityChecksFilters>
  | Partial<PropertiesFilters>
  | Partial<JobsFilters>
  | Partial<ScheduledCommunicationsFilters>
  | Partial<NotificationAlertsFilters>

export type FilterStateAction = {
  payload: Partial<FiltersContextState>
}

export interface FiltersContextType extends FiltersContextState {
  clearFilters: (filterName: FilterNames) => void
  clearReservationMoveInRange: () => void
  clearReservationMoveOutRange: () => void
  setFilters: (
    filterKey: FilterNames,
    filters:
      | Partial<ReservationsFilters>
      | Partial<HomesFilters>
      | Partial<HomeQualityChecksFilters>
      | Partial<PropertiesFilters>
      | Partial<JobsFilters>
      | Partial<ScheduledCommunicationsFilters>
      | Partial<NotificationAlertsFilters>,
  ) => void

  setMarketSegments: (segmentIds: number[]) => void
  setReservationMinLengthOfStay: (val?: number) => void
  setReservationMoveInRange: (r: Partial<ReservationMoveInRange>) => void
  setReservationMoveOutRange: (r: Partial<ReservationMoveOutRange>) => void
  // ONLY USE WHEN NEED DIRECT/PROGRAMMATIC CONTROL
  setSearchString: (val: string) => void
}

export const getPreviousSavedFilters = (parsedState: FiltersContextState) => {
  const validFilters = FiltersContextStateSchema.safeParse(parsedState)
  if (validFilters.success) {
    return parsedState
  } else {
    // eslint-disable-next-line no-console
    console.log(validFilters.error)
    return InitialFilterState
  }
}
